/**
 * KUROGANE — utilidades compartidas.
 * Sin dependencias. Se expone en window.kg para que los componentes la consuman.
 * Contrato documentado en docs/ARCHITECTURE.md §8.
 */
(function () {
  'use strict';

  const theme = window.kgTheme || {};

  /* ------------------------------------------------------------------ *
   * Movimiento
   * ------------------------------------------------------------------ */
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const prefersReducedMotion = () => reducedMotionQuery.matches;

  /* ------------------------------------------------------------------ *
   * Red
   * ------------------------------------------------------------------ */
  async function fetchJSON(url, options = {}) {
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...options
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    if (!response.ok) {
      const message =
        (payload && (payload.description || payload.message)) ||
        (theme.strings && theme.strings.cartError) ||
        'Request failed';
      const error = new Error(message);
      error.status = response.status;
      error.payload = payload;
      throw error;
    }

    return payload;
  }

  async function fetchSection(url, selector) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    return selector ? html.querySelector(selector) : html;
  }

  /* ------------------------------------------------------------------ *
   * Formato
   * ------------------------------------------------------------------ */
  function formatMoney(cents, format) {
    const pattern = format || theme.moneyFormat || '${{amount}}';
    const value = typeof cents === 'string' ? cents.replace('.', '') : cents;

    function withDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number === null) return '0';
      const fixed = (number / 100.0).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const cents_ = parts[1] ? decimal + parts[1] : '';
      return dollars + cents_;
    }

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = pattern.match(placeholderRegex);
    if (!match) return pattern;

    let formatted;
    switch (match[1]) {
      case 'amount': formatted = withDelimiters(value, 2); break;
      case 'amount_no_decimals': formatted = withDelimiters(value, 0); break;
      case 'amount_with_comma_separator': formatted = withDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': formatted = withDelimiters(value, 0, '.', ','); break;
      case 'amount_with_space_separator': formatted = withDelimiters(value, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': formatted = withDelimiters(value, 0, ' ', ','); break;
      case 'amount_with_apostrophe_separator': formatted = withDelimiters(value, 2, "'", '.'); break;
      default: formatted = withDelimiters(value, 2);
    }
    return pattern.replace(placeholderRegex, formatted);
  }

  /* ------------------------------------------------------------------ *
   * Foco
   * ------------------------------------------------------------------ */
  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), iframe, object, embed, ' +
    '[tabindex]:not([tabindex="-1"]), [contenteditable="true"], summary, details > summary:first-of-type';

  const trapState = new WeakMap();

  function getFocusable(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  function trapFocus(container, elementToFocus) {
    releaseFocus(container);

    const previous = document.activeElement;

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      const focusable = getFocusable(container);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // Si el foco escapa del contenedor (p. ej. clic fuera), lo devolvemos.
    function onFocusIn(event) {
      if (!container.contains(event.target)) {
        const focusable = getFocusable(container);
        (focusable[0] || container).focus();
      }
    }

    document.addEventListener('keydown', onKeydown);
    document.addEventListener('focusin', onFocusIn);
    trapState.set(container, { onKeydown, onFocusIn, previous });

    const target = elementToFocus || getFocusable(container)[0] || container;
    // Esperamos al fin de la transición de apertura para no romper el scroll.
    window.requestAnimationFrame(() => target.focus({ preventScroll: true }));
  }

  function releaseFocus(container, restore = false) {
    const state = trapState.get(container);
    if (!state) return;
    document.removeEventListener('keydown', state.onKeydown);
    document.removeEventListener('focusin', state.onFocusIn);
    trapState.delete(container);
    if (restore && state.previous && typeof state.previous.focus === 'function') {
      state.previous.focus({ preventScroll: true });
    }
  }

  /* ------------------------------------------------------------------ *
   * Bloqueo de scroll (sin salto de layout)
   * ------------------------------------------------------------------ */
  let scrollLocks = 0;

  function lockScroll() {
    scrollLocks += 1;
    if (scrollLocks > 1) return;
    const width = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--kg-scrollbar-width', `${width}px`);
    document.body.classList.add('kg-scroll-locked');
  }

  function unlockScroll(force = false) {
    scrollLocks = force ? 0 : Math.max(0, scrollLocks - 1);
    if (scrollLocks > 0) return;
    document.body.classList.remove('kg-scroll-locked');
    document.documentElement.style.removeProperty('--kg-scrollbar-width');
  }

  /* ------------------------------------------------------------------ *
   * Overlay global compartido
   * ------------------------------------------------------------------ */
  function overlayEl() {
    return document.querySelector('[data-kg-overlay]');
  }

  function showOverlay(onClick) {
    const el = overlayEl();
    if (!el) return;
    el.hidden = false;
    // Forzamos reflow para que la transición se aplique.
    void el.offsetWidth;
    el.dataset.open = 'true';
    if (onClick) {
      el._kgHandler = onClick;
      el.addEventListener('click', onClick, { once: true });
    }
  }

  function hideOverlay() {
    const el = overlayEl();
    if (!el) return;
    delete el.dataset.open;
    if (el._kgHandler) {
      el.removeEventListener('click', el._kgHandler);
      delete el._kgHandler;
    }
    window.setTimeout(() => {
      if (el.dataset.open !== 'true') el.hidden = true;
    }, 400);
  }

  /* ------------------------------------------------------------------ *
   * Anuncios para lectores de pantalla
   * ------------------------------------------------------------------ */
  function announce(message) {
    const region = document.getElementById('kg-live-region');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 60);
  }

  /* ------------------------------------------------------------------ *
   * Tiempos
   * ------------------------------------------------------------------ */
  function debounce(fn, wait = 250) {
    let timer;
    return function (...args) {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function throttle(fn, limit = 100) {
    let waiting = false;
    return function (...args) {
      if (waiting) return;
      fn.apply(this, args);
      waiting = true;
      window.setTimeout(() => { waiting = false; }, limit);
    };
  }

  /* ------------------------------------------------------------------ *
   * Intersección
   * ------------------------------------------------------------------ */
  function onIntersect(elements, callback, options = {}) {
    const list = elements instanceof Element ? [elements] : Array.from(elements);
    if (!('IntersectionObserver' in window)) {
      list.forEach((el) => callback(el));
      return null;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        callback(entry.target, observer);
        if (options.once !== false) observer.unobserve(entry.target);
      });
    }, { rootMargin: options.rootMargin || '0px 0px -10% 0px', threshold: options.threshold || 0 });

    list.forEach((el) => observer.observe(el));
    return observer;
  }

  /* ------------------------------------------------------------------ *
   * Bus del carrito
   * ------------------------------------------------------------------ */
  const CART_EVENT = 'kg:cart:updated';

  function publishCart(cart, source) {
    document.dispatchEvent(new CustomEvent(CART_EVENT, { detail: { cart, source } }));
  }

  /* ------------------------------------------------------------------ *
   * Export
   * ------------------------------------------------------------------ */
  window.kg = {
    fetchJSON,
    fetchSection,
    formatMoney,
    trapFocus,
    releaseFocus,
    getFocusable,
    lockScroll,
    unlockScroll,
    showOverlay,
    hideOverlay,
    announce,
    debounce,
    throttle,
    onIntersect,
    prefersReducedMotion,
    publishCart,
    CART_EVENT,
    routes: theme.routes || {},
    strings: theme.strings || {}
  };
})();
